import "../../index.css";

import { useContext } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import type { CarrierSelectionPageLoadData } from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import type { MarginCalculationData } from "~/features/quote/utils/margin-calculator.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type {
	BookTenderResponse,
	QuoteResponse,
} from "~/shared/schemas/quote-schema.ts";
import type {
	CarrierFiltersType,
	FilterKey,
	SortDirection,
} from "~/shared/types/carrier-selection-types.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";

import { CarrierCard } from "../carrier-card/carrier-card.tsx";
import { CarrierHeader } from "../carrier-header/carrier-header.tsx";
import { CarrierSelectionHeader } from "../carrier-selection-header/carrier-selection-header.tsx";
import { LiabilityWarningBanner } from "../shared/liability-warning-banner.tsx";
import { RoutingGuideBanner } from "../shared/routing-guide-banner.tsx";

export type CarrierSelectionPanelProps = {
	carrierPrices: CarrierPrice[];
	displayedCarriers: CarrierPrice[];
	pageData: CarrierSelectionPageLoadData | undefined;
	wrapperClassName?: string;
	isAddInsuranceEnabled: boolean;
	isSaveQuoteEnabled: boolean;
	isAdjustingPrices: boolean;
	isRemovingInsurance: boolean;
	isUpdatingMargins: boolean;
	addBlankCarrier: () => void;
	marginType: "percentage" | "flat";
	marginValue: number;
	onAdjustClick: (quoteState: QuoteStateType) => void;
	onMarginTypeChange: (type: "percentage" | "flat") => void;
	onRemoveInsurance: () => void;
	onSetMarginValue: (value: number) => void;
	onToggleModal: (modal: string) => void;
	onUpdateAllCarrierMargins: (
		quoteId: number,
		eTag: string,
		carriers: CarrierPrice[],
	) => void;
	onFilterChange: <K extends FilterKey>(
		key: K,
		value: CarrierFiltersType[K],
	) => void;
	onTabSelect: (tab: number) => void;
	onSortDirectionChange: (direction: SortDirection) => void;
	sortDirection: SortDirection;
	showLiabilityBanner?: boolean;
	showRoutingGuideBanner?: boolean;
	hasRoutingGuides?: boolean;
	isGuideActive: boolean;
	trigger?: string | null;
	hiddenCarrierCount: number;
	showingHidden: boolean;
	onToggleGuide: () => void;
	onToggleHiddenCarriers: () => void;
	isBookingDisabled: (carrierPrice: CarrierPrice) => boolean;
	onStepChange: () => void;
	onBookingStart?: () => void;
	onBookingEnd?: () => void;
	onTenderSuccess?: (tenderedQuote: BookTenderResponse) => void;
	onTenderError?: (message: string) => void;
	onPaymentRequired?: (quote: QuoteResponse) => void;
	marginCalculationData?: MarginCalculationData;
	enableShipmentValueModal?: boolean;
	enableTenderOnBook?: boolean;
	isRerate?: boolean;
	selectedCarrierPriceId?: number | null;
	onSelectCarrier?: (carrier: CarrierPrice) => void;
};

const getCarrierKey = (carrier: CarrierPrice): string =>
	`carrier-${carrier.id}-${carrier.quoteId}-${carrier.carrierId}-${carrier.carrier?.id ?? ""}${carrier.isAdHoc ? "-adhoc" : ""}`;

export const CarrierSelectionPanel = ({
	carrierPrices,
	displayedCarriers,
	pageData,
	wrapperClassName,
	isAddInsuranceEnabled,
	isSaveQuoteEnabled,
	isAdjustingPrices,
	isRemovingInsurance,
	isUpdatingMargins,
	addBlankCarrier,
	marginType,
	marginValue,
	onAdjustClick,
	onMarginTypeChange,
	onRemoveInsurance,
	onSetMarginValue,
	onToggleModal,
	onUpdateAllCarrierMargins,
	onFilterChange,
	onTabSelect,
	onSortDirectionChange,
	sortDirection,
	showLiabilityBanner = false,
	showRoutingGuideBanner = true,
	hasRoutingGuides = false,
	isGuideActive,
	trigger = null,
	hiddenCarrierCount,
	showingHidden,
	onToggleGuide,
	onToggleHiddenCarriers,
	isBookingDisabled,
	onStepChange,
	onBookingStart,
	onBookingEnd,
	onTenderSuccess,
	onTenderError,
	onPaymentRequired,
	marginCalculationData,
	enableShipmentValueModal = false,
	enableTenderOnBook = false,
	isRerate = false,
	selectedCarrierPriceId,
	onSelectCarrier,
}: Readonly<CarrierSelectionPanelProps>) => {
	const { quoteState } = useContext(QuoteDetailsContext);

	return (
		<div className={wrapperClassName} id={"carrier-selection-wrapper"}>
			<CarrierSelectionHeader
				adHocCarrierExists={carrierPrices.some(
					(carrier) => carrier.isAdHoc && carrier.carrier.id < 1,
				)}
				addBlankCarrier={addBlankCarrier}
				handleAdjustClick={onAdjustClick}
				handleMarginTypeChange={onMarginTypeChange}
				handleRemoveInsuranceClick={onRemoveInsurance}
				isAddInsuranceEnabled={isAddInsuranceEnabled}
				isAdjustingPrices={isAdjustingPrices}
				isRemovingInsurance={isRemovingInsurance}
				isSaveQuoteEnabled={isSaveQuoteEnabled}
				isUpdatingMargins={isUpdatingMargins}
				marginType={marginType}
				marginValue={marginValue}
				quoteState={quoteState}
				setMarginValue={onSetMarginValue}
				toggleModal={onToggleModal}
				updateAllCarrierMargins={onUpdateAllCarrierMargins}
			/>
			<CarrierHeader
				carrierPrices={carrierPrices}
				onFilterChange={onFilterChange}
				onSortDirectionChange={onSortDirectionChange}
				onTabSelect={onTabSelect}
				sortDirection={sortDirection}
			/>
			{showLiabilityBanner ? <LiabilityWarningBanner /> : null}
			<div>
				<div id={"carrier-cards-wrapper"}>
					{displayedCarriers.map((carrier) => (
						<CarrierCard
							key={getCarrierKey(carrier)}
							bannerType={trigger}
							carrierPrice={carrier}
							enableShipmentValueModal={enableShipmentValueModal}
							enableTenderOnBook={enableTenderOnBook}
							isBookingDisabled={isBookingDisabled(carrier)}
							isRerate={isRerate}
							isSelected={selectedCarrierPriceId === carrier.id}
							marginCalculationData={marginCalculationData}
							onBookingEnd={onBookingEnd}
							onBookingStart={onBookingStart}
							onPaymentRequired={onPaymentRequired}
							onSelectCarrier={onSelectCarrier}
							onStepChange={onStepChange}
							onTenderError={onTenderError}
							onTenderSuccess={onTenderSuccess}
							pageData={pageData}
						/>
					))}
				</div>
				{showRoutingGuideBanner ? (
					<RoutingGuideBanner
						hasRoutingGuides={hasRoutingGuides}
						hiddenCarrierCount={hiddenCarrierCount}
						isGuideActive={isGuideActive}
						onToggleGuide={onToggleGuide}
						onToggleHiddenCarriers={onToggleHiddenCarriers}
						showingHidden={showingHidden}
					/>
				) : null}
			</div>
		</div>
	);
};
